import { useState, useEffect, useCallback } from 'react'
import { Send, User, Shield, Eye } from 'lucide-react'
import axios from 'axios'
import { emailService } from '../services/emailService'
import type { EmailTemplateInfo } from '../types'
import { LoadingState } from '@/shared/components/ui/atoms/LoadingState'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'

interface SendState {
  templateId: string
  status: 'idle' | 'sending' | 'success' | 'error'
  message: string | null
}

export function EmailCatalog() {
  const [destinationEmail, setDestinationEmail] = useState('')
  const [sendStates, setSendStates] = useState<Record<string, SendState>>({})
  const [templates, setTemplates] = useState<EmailTemplateInfo[]>([])
  const [emailFlags, setEmailFlags] = useState<Record<string, boolean>>({})
  const [togglingFlags, setTogglingFlags] = useState<Record<string, boolean>>({})
  const [previewingIds, setPreviewingIds] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setLoadError(null)
      try {
        const [templatesData, flagsData] = await Promise.all([
          emailService.getTemplates(),
          emailService.getEmailFlags(),
        ])
        setTemplates(templatesData)
        setEmailFlags(flagsData)
      } catch (err) {
        setLoadError(
          err instanceof Error ? err.message : 'Error al cargar templates',
        )
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  const updateSendState = (templateId: string, update: Partial<Omit<SendState, 'templateId'>>) => {
    setSendStates((prev) => {
      const current = prev[templateId] ?? { templateId, status: 'idle' as const, message: null }
      return {
        ...prev,
        [templateId]: { ...current, ...update },
      }
    })
  }

  const handleSend = async (template: EmailTemplateInfo) => {
    if (!destinationEmail.trim()) return

    updateSendState(template.id, { status: 'sending', message: null })
    try {
      const result = await emailService.sendTestTemplate(
        template.id,
        destinationEmail.trim(),
      )
      const isSuccess = result.includes('successfully') || result.includes('enviado')
      updateSendState(template.id, {
        status: 'success',
        message: isSuccess ? 'Enviado' : result,
      })
    } catch (err) {
      let message = 'Error al enviar'
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        if (typeof detail === 'string') message = detail
      }
      updateSendState(template.id, { status: 'error', message })
    }
  }

  const handleToggleFlag = useCallback(async (templateId: string, currentEnabled: boolean) => {
    setTogglingFlags((prev) => ({ ...prev, [templateId]: true }))
    try {
      const newEnabled = !currentEnabled
      const updatedFlags = await emailService.updateEmailFlag(templateId, newEnabled, emailFlags)
      setEmailFlags(updatedFlags)
    } catch {
      // revert on error - do nothing, flags stay as they were
    } finally {
      setTogglingFlags((prev) => ({ ...prev, [templateId]: false }))
    }
  }, [emailFlags])

  const handlePreview = useCallback(async (templateId: string) => {
    setPreviewingIds((prev) => ({ ...prev, [templateId]: true }))
    try {
      await emailService.openPreview(templateId)
    } catch {
      // Silently fail — user will notice the tab didn't open
    } finally {
      setPreviewingIds((prev) => ({ ...prev, [templateId]: false }))
    }
  }, [])

  const isValidEmail = destinationEmail.trim().includes('@')

  if (isLoading) return <LoadingState />
  if (loadError) return <ErrorState message={loadError} />

  return (
    <div className="space-y-4">
      {/* Destination email input (shared for all templates) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email de destino para pruebas *
        </label>
        <input
          type="email"
          value={destinationEmail}
          onChange={(e) => setDestinationEmail(e.target.value)}
          className="input max-w-md"
          placeholder="correo@ejemplo.com"
        />
        <p className="text-xs text-gray-500 mt-1">
          Todos los emails de prueba se enviarán a esta dirección con data de ejemplo.
        </p>
      </div>

      {/* Template list */}
      <div className="space-y-3">
        {templates.map((template) => {
          const state = sendStates[template.id]
          const isSending = state?.status === 'sending'
          const isEnabled = emailFlags[template.id] !== false
          const isToggling = togglingFlags[template.id] ?? false
          const isPreviewing = previewingIds[template.id] ?? false

          return (
            <div
              key={template.id}
              className={`flex items-start gap-4 p-4 border rounded-lg transition-colors ${
                isEnabled
                  ? 'border-gray-200 bg-white'
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                template.recipient === 'client'
                  ? 'bg-blue-50'
                  : 'bg-amber-50'
              }`}>
                {template.recipient === 'client' ? (
                  <User size={16} className="text-blue-600" />
                ) : (
                  <Shield size={16} className="text-amber-600" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`text-sm font-medium ${isEnabled ? 'text-gray-900' : 'text-gray-400'}`}>
                    {template.name}
                  </h3>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    template.recipient === 'client'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {template.recipient === 'client' ? 'Cliente' : 'Admin'}
                  </span>
                  {!isEnabled && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-600">
                      Desactivado
                    </span>
                  )}
                </div>
                <p className={`text-xs mb-1 ${isEnabled ? 'text-gray-500' : 'text-gray-400'}`}>
                  {template.description}
                </p>
                <p className={`text-xs ${isEnabled ? 'text-gray-400' : 'text-gray-300'}`}>
                  Asunto: <span className="font-medium">{template.subject}</span>
                </p>

                {/* Feedback per template */}
                {state?.status === 'success' && (
                  <p className="text-xs text-green-600 mt-1">{state.message}</p>
                )}
                {state?.status === 'error' && (
                  <p className="text-xs text-red-600 mt-1">{state.message}</p>
                )}
              </div>

              {/* Toggle + Send button */}
              <div className="flex-shrink-0 flex items-center gap-3">
                {/* Toggle switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={isEnabled}
                  aria-label={`${isEnabled ? 'Desactivar' : 'Activar'} ${template.name}`}
                  disabled={isToggling}
                  onClick={() => handleToggleFlag(template.id, isEnabled)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                    isEnabled ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>

                {/* Preview button */}
                <button
                  onClick={() => handlePreview(template.id)}
                  disabled={isPreviewing}
                  className="flex-shrink-0 btn btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50"
                  title={`Vista previa de "${template.name}"`}
                >
                  <Eye size={14} />
                  {isPreviewing ? 'Cargando...' : 'Preview'}
                </button>

                {/* Send test button */}
                <button
                  onClick={() => handleSend(template)}
                  disabled={isSending || !isValidEmail}
                  className="flex-shrink-0 btn btn-secondary text-sm flex items-center gap-1.5 disabled:opacity-50"
                  title={!isValidEmail ? 'Ingresa un email de destino válido' : `Enviar "${template.subject}"`}
                >
                  <Send size={14} />
                  {isSending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
